/**
 * Reverse-parses the app's routes to work out what the user is looking at, so
 * the dialog can offer job-shaped prompts and the agent can resolve "this page".
 *
 * Route-based rather than DOM-based: scraping the live page would bind context
 * to CSS class names, which rename without warning and fail silently.
 */

export interface IOpenRecord {
    /** Entity kind, as mapped from the route segment by `RECORD_ROUTES`. */
    entity: string;
    id: string;
}

/** Route segment -> entity kind. Single-record routes only; a list page has
 * no record to report. */
const RECORD_ROUTES: Record<string, string> = {
    bill: "bill",
    bills: "bill",
    job: "job",
    jobs: "job",
    // The job detail route is /JobPage/:jobId/:defaultTab.
    jobpage: "job",
    lead: "lead",
    leads: "lead",
    changeorders: "changeorder",
    changeorder: "changeorder",
    dailylog: "dailylog",
    dailylogs: "dailylog",
    purchaseorder: "purchaseorder",
    purchaseorders: "purchaseorder",
    selection: "selection",
    selections: "selection",
    warranty: "warranty",
    warranties: "warranty",
    rfi: "rfi",
    rfis: "rfi",
    schedule: "schedule",
    todo: "todo",
    todos: "todo",
    contact: "contact",
    contacts: "contact",
    sub: "sub",
    subs: "sub",
    ownerinvoice: "ownerinvoice",
    bidpackage: "bidpackage",
    bidpackages: "bidpackage",
};

/**
 * The record the current path has open, or null.
 *
 * Ids must be digits, so list and sub-pages like `/Jobs/Map` don't report a
 * record. Routes with string ids (to-dos) report none.
 */
export function parseOpenRecord(pathname: string): IOpenRecord | null {
    const segments = pathname
        .split("/")
        .filter((segment) => segment.length > 0)
        .filter((segment) => segment.toLowerCase() !== "app");

    if (segments.length < 2) {
        return null;
    }

    const entity = RECORD_ROUTES[segments[0].toLowerCase()];
    if (entity === undefined) {
        return null;
    }

    const id = segments[1];
    if (!/^\d+$/.test(id)) {
        return null;
    }

    return { entity, id };
}

/** The record as one query-string value, `kind:id`. */
export function formatOpenRecordParam(record: IOpenRecord): string {
    return `${record.entity}:${record.id}`;
}

const OPEN_RECORD_ENTITIES = new Set(Object.values(RECORD_ROUTES));

/**
 * Reads a {@link formatOpenRecordParam} value back, or null. It arrives in a
 * URL, so it is only trusted in the shape {@link parseOpenRecord} produces.
 */
export function parseOpenRecordParam(value: string | null): IOpenRecord | null {
    const match = /^([a-z]+):(\d+)$/.exec(value ?? "");
    if (match === null || !OPEN_RECORD_ENTITIES.has(match[1])) {
        return null;
    }
    return { entity: match[1], id: match[2] };
}
